import React from "react";
import { Paper, Stack, Typography } from "@mui/material";
import { ClockCounterClockwise } from "@phosphor-icons/react";

import { FILTERS, generateDynamicLabels, getDataArraysByCode } from "../utils";
import { ModuleContainer } from "../styles";
import ModuleHeader from "../ModuleHeader";
import DeliveredCharts from "./DeliveredCharts";
import EmptyDeliveredHours from "./EmptyDeliveredHours";
import PeriodToggleButton from "../PeriodToggleButton";
import ClientSelect from "../ClientSelect";
/** @typedef {import("api/typesDef").Client} Client */
/** @typedef {import("api/typesDef").DeliveredHours} DeliveredHours */

/**
 * @component
 * @description `DeliveredHours` component to display delivered hours by client and time unit
 * (weeks or months) in a bar chart.
 * @param {Object} props
 * @param {Client[]} props.clients - The clients
 * @param {DeliveredHours[]} props.deliveredHours - The delivered hours
 * @param {string} props.period - The period
 * @param {function} props.onPeriodChange - The function to change the period
 * @param {Client} props.selectedClient - The selected client
 * @param {function} props.onClientChange - The function to change the selected client
 * @param {boolean} props.isSmallScreen - Whether the screen is small
 * @returns {JSX.Element} Delivered hours by client and time unit
 */
const DeliveredHoursModule = ({
  clients,
  deliveredHours,
  period,
  selectedClient,
  onPeriodChange = () => {},
  onClientChange = () => {},
  isSmallScreen,
}) => {
  const isEmpty = !deliveredHours || deliveredHours?.length === 0;
  // get total_duration of the last weeks or months (because the -1 is the current week or month)
  const estimatedHours = deliveredHours?.at(-2)?.total_duration;
  const textOfLast = period === FILTERS.BY_WEEKS ? "last week" : "last month";
  // get the data for the charts
  const dataArraysByCode = getDataArraysByCode(deliveredHours);
  const datesLabels = generateDynamicLabels(deliveredHours, period, isSmallScreen);

  // If all total_duration are 0, show the empty state
  const allTotalDurationAreZero = dataArraysByCode.every((data) =>
    data.totalDuration.every((duration) => duration === 0)
  );

  return (
    <Paper
      component="section"
      elevation={2}
      sx={ModuleContainer}
      data-testid="delivered-hours-module"
    >
      <Stack
        direction={isSmallScreen ? "column" : "row"}
        justifyContent="space-between"
        alignItems="baseline"
        mb={7}
      >
        <ModuleHeader
          Icon={ClockCounterClockwise}
          title="Delivered Hours"
          subtitle={
            isEmpty || allTotalDurationAreZero ? null : selectedClient === "all" ? (
              <>
                You delivered{" "}
                <Typography component="span" color="primary.main" variant="subtitle2">
                  {estimatedHours}
                </Typography>{" "}
                hours {textOfLast}.
              </>
            ) : (
              <>
                {selectedClient.name} received{" "}
                <Typography component="span" color="primary.main" variant="subtitle2">
                  {estimatedHours}
                </Typography>{" "}
                hours of therapy {textOfLast}.
              </>
            )
          }
        />

        <Stack
          direction="row"
          sx={{ width: isSmallScreen ? "100%" : "auto" }}
          gap={3}
          mt={{ xs: 5, md: 0 }}
          justifyContent="flex-end"
        >
          {!isSmallScreen && (
            <ClientSelect
              clients={clients}
              selectedClient={selectedClient}
              onClientChange={onClientChange}
              data-testid="client-select-top"
            />
          )}
          <PeriodToggleButton
            period={period}
            onPeriodChange={onPeriodChange}
            isSmallScreen={isSmallScreen}
          />
        </Stack>
      </Stack>

      {isEmpty || allTotalDurationAreZero ? (
        <EmptyDeliveredHours />
      ) : (
        <Stack sx={{ minHeight: "200px", maxHeight: isSmallScreen ? "200px" : "300px" }}>
          <DeliveredCharts period={period} data={dataArraysByCode} labels={datesLabels} />
        </Stack>
      )}

      {isSmallScreen && !isEmpty && (
        <ClientSelect
          fullWidth
          clients={clients}
          selectedClient={selectedClient}
          onClientChange={onClientChange}
          data-testid="client-select-bottom"
        />
      )}
    </Paper>
  );
};

export default DeliveredHoursModule;
